import React, { useCallback, useState } from "react";
import moment from "moment";
import {
  Grid,
  TableRow,
  TableCell,
  Table,
  TableContainer,
  TableBody,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Tooltip,
  IconButton,
  Tab,
} from "@material-ui/core";
import { EnhancedTableHead } from "./index";
import excelIcon from "../../../../assets/images/icons/excel.svg";
import axios from "../../../../axios";

// ✅ Updated Columns
const interpreterHeadCells = [
  { id: "i", label: "Sl.No" },
  { id: "booking_ref", label: "Booking Ref" },
  { id: "interpreter", label: "Interpreter" },
  { id: "interpreterBusinessName", label: "Business Name / VAT No" },
  { id: "status", label: "Status" },
  { id: "company", label: "Company Name" },
  { id: "date", label: "Attendance Date" },
  { id: "invoiceNo", label: "Invoice No / Date" },
  { id: "language", label: "Language / Service" },
  { id: "client", label: "Client Name" },
  { id: "amount", label: "Amount" },
  { id: "vatAmount", label: "VAT" },
  { id: "invoiceTotal", label: "Invoice Total" },
];

const styles = {
  fontSize: "13px",
  padding: "6px",
};

const CardItem = ({
  active,
  classes,
  data,
  isLoading,
  matches,
  title,
  values,
  hasData,
}) => {
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("company");
  const [downloading, setDownloading] = useState(false);

  const handleRequestSort = useCallback(
    (event, property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [order, orderBy],
  );

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const newSelecteds = data.map((n) => n._id);
        setSelected(newSelecteds);
        return;
      }
      setSelected([]);
    },
    [data],
  );

  const downloadHandler = async () => {
    try {
      setDownloading(true);

      const params = new URLSearchParams();

      if (values.company?._id) {
        params.append("companyId", values.company._id);
      }

      if (values.interpreter?._id) {
        params.append("interpreter", values.interpreter._id);
      }

      if (values.businessType) {
        params.append("businessType", values.businessType);
      }

      if (values.status) {
        params.append("status", values.status);
      }

      // VAT checkbox
      if (values.hasVat) {
        params.append("hasVat", "true");
      }

      // Interpreter VAT No
      if (values.vatNo) {
        params.append("interpreterVatNumber", values.vatNo);
      }

      // Interpreter Business Name
      if (values.businessName) {
        params.append("interpreterBusinessName", values.businessName);
      }

      // From Date
      if (values.fromDate) {
        const fromDate = new Date(values.fromDate);

        if (!Number.isNaN(fromDate.getTime())) {
          params.append("fromDate", fromDate.toISOString());
        }
      }

      // To Date
      if (values.toDate) {
        const toDate = new Date(values.toDate);

        if (!Number.isNaN(toDate.getTime())) {
          params.append("toDate", toDate.toISOString());
        }
      }

      const query = params.toString();

      console.log("Excel Download Query:", query);

      const response = await axios.get(
        `/api/v1/bookings/ipinvoicereport/download?${query}`,
        {
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement("a");

      link.href = url;

      link.setAttribute(
        "download",
        `InterpreterInvoiceReport_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading Interpreter Invoice Report:", err);
    } finally {
      setDownloading(false);
    }
  };

  console.log(data, "data");

  return (
    <Card className={active}>
      <CardContent style={{ padding: "8px 6px" }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          style={{ padding: "6px" }}
        >
          {/* Left: Title */}
          <Grid item xs>
            <Typography variant="body1" noWrap>
              {title}
            </Typography>
          </Grid>

          {/* Right: Excel Icon */}
          {hasData && (
            <Grid item>
              <Tooltip title="Download Excel">
                <span>
                  <IconButton
                    size="medium"
                    cursor="pointer"
                    onClick={downloadHandler}
                    disabled={downloading || data.length === 0}
                  >
                    <img
                      src={excelIcon}
                      alt="excel"
                      style={{ width: 25, height: 25 }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          )}
        </Grid>

        <TableContainer
          className={matches ? classes.container2 : classes.container}
        >
          <Table stickyHeader size="small">
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={data.length}
              headCells={interpreterHeadCells}
            />

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No record
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, index) => {
                  return (
                    <TableRow hover key={row._id || index}>
                      {/* Sl.No */}
                      <TableCell style={styles}>{index + 1}</TableCell>

                      {/* Booking Ref */}
                      <TableCell style={styles}>{row.booking_ref}</TableCell>

                      {/* Interpreter */}
                      <TableCell style={styles}>
                        <b>{row.interpreter}</b>
                        {row.interpreterRef && (
                          <>
                            <br />
                            <small>{row.interpreterRef}</small>
                          </>
                        )}
                      </TableCell>

                      <TableCell style={styles}>
                        <b>{row.interpreterBusinessName || ""}</b> <br />
                        <small>{row.interpreterVatNumber || ""}</small>
                      </TableCell>

                      {/* Status */}
                      <TableCell style={styles}>{row.status}</TableCell>

                      {/* Company Name */}
                      <TableCell style={styles}>
                        <b>
                          {row.businessType === "Private"
                            ? "Private"
                            : row.companyName}
                        </b>
                      </TableCell>

                      {/* Date */}
                      <TableCell style={styles}>
                        {row.date ? moment(row.date).format("DD/MM/YYYY") : ""}
                      </TableCell>

                      {/* Invoice No / Date */}
                      <TableCell style={styles} align="left">
                        {row.invoiceNo}
                        <br />
                        <small>
                          {row.invoiceDate
                            ? moment(row.invoiceDate).format("DD/MM/YYYY")
                            : ""}
                        </small>
                      </TableCell>

                      {/* Language / Service */}
                      <TableCell style={styles} align="left">
                        <strong>{row.language}</strong> <br />
                        <small>{row.serviceType}</small>
                      </TableCell>

                      {/* Client Name */}
                      <TableCell style={styles}>{row.clientName}</TableCell>

                      {/* Amount */}
                      <TableCell style={styles} align="left">
                        {row.amount != null
                          ? Number(row.amount).toFixed(2)
                          : "0.00"}
                      </TableCell>

                      {/* VAT */}
                      <TableCell style={styles} align="left">
                        {row.vatAmount != null
                          ? Number(row.vatAmount).toFixed(2)
                          : "0.00"}
                      </TableCell>

                      {/* Invoice Total */}
                      <TableCell style={styles} align="left">
                        <strong>
                          {row.invoiceTotal != null
                            ? Number(row.invoiceTotal).toFixed(2)
                            : "0.00"}
                        </strong>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default React.memo(CardItem);
